import { useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'

import { BankAccount } from '@features/bankAccounts/types/bankAccount'
import { useRenewOpenBankingConnection } from '@features/openBanking/hooks/useRenewOpenBankingConnection'
import { OpenBankingAccount } from '@features/openBanking/types/openBankingAccount'
import { isOpenBankingAccountDeleted } from '@features/openBanking/utils/isOpenBankingAccountDeleted'

import { ModalId } from '../../../../../../../../enums/modalId'
import { CardActionType } from '../../enums/cardActionType'
import { getOpenBankingAccountStatus } from '../../utils/getOpenBankingAccountStatus'
import { BaseAccountCard } from '../BaseAccountCard'

interface OpenBankingAccountCardProps {
  accountNo?: number
  openBankingAccount: OpenBankingAccount
  bankAccount?: BankAccount
}

export const OpenBankingAccountCard = ({
  accountNo,
  openBankingAccount,
  bankAccount,
}: OpenBankingAccountCardProps): ReactElement => {
  const { bank, name, iban, balance, currency, connection, externalAccountId, lastSynchronizedAt, isOrphaned } =
    openBankingAccount
  const { open: openOrphanedModal } = useModal(ModalId.BankAccountOrphanedModal)
  const { renewOpenBankingConnection, isRenewing } = useRenewOpenBankingConnection()

  const isDeleted = isOpenBankingAccountDeleted(openBankingAccount)
  const syncStatus = getOpenBankingAccountStatus(openBankingAccount)
  const canRemoveConnection = !isDeleted && !connection.requiresUpdate

  const actionItems = useMemo(
    () => [
      CardActionType.UploadToAccount,
      CardActionType.DownloadTransactions,
      CardActionType.EditAccountName,
      CardActionType.HideAccount,
      ...(canRemoveConnection ? [CardActionType.RemoveConnection] : []),
    ],
    [canRemoveConnection],
  )

  const handleRenew = useCallback(() => {
    if (isOrphaned) {
      return openOrphanedModal()
    }
    renewOpenBankingConnection(openBankingAccount.id)
  }, [isOrphaned, openBankingAccount.id, openOrphanedModal, renewOpenBankingConnection])

  return (
    <BaseAccountCard
      account={{
        accountName: name,
        bankAccountId: bankAccount?.id,
        customAccountName: bankAccount?.customAccountName || null,
        iban,
        externalAccountId: externalAccountId ?? undefined,
        accountNo,
        totalAmount: isDeleted ? undefined : balance,
        currency,
      }}
      bankLogo={bank.presentation ?? undefined}
      bankName={bank.name}
      syncStatus={syncStatus}
      syncDate={lastSynchronizedAt}
      actionItems={actionItems}
      isRenewing={isRenewing}
      onRenew={handleRenew}
    />
  )
}
